/**
 * @fileoverview Connect selected matter attorney context to explicit calendar-draft assignment. The backend supplies available and matter-linked attorneys; Redux owns the draft's assignee list. Defaults and manually chosen responsibility remain reviewable form state until the enclosing event save is accepted.
 */

import { useSelector, useDispatch } from 'react-redux';
import api from '../../api';
import { useEffect, useState } from 'react';
import {
  setAssignedAttorneys,
  setAvailableAttorneys,
  setMatterAttorneys } from
'../../store/CalendarInput/action';
import { MultiSelect } from 'primereact/multiselect';
import Button from "../shared/AppPrimeButton";
import { Badge } from 'primereact/badge';
import AppIcon from "../shared/AppIcon";



const AssigneeManagement = () => {
  const dispatch = useDispatch();
  const matterNumber = useSelector((state) => state.calendarInput.matterNumber);
  const assignedAttorneys = useSelector((state) => state.calendarInput.assignedAttorneys);
  const availableAttorneys = useSelector((state) => state.calendarInput.availableAttorneys);
  const matterAttorneys = useSelector((state) => state.calendarInput.matterAttorneys);
  const currentlyEditingEvent = useSelector((state) => state.calendarInput.currentlyEditingEvent);

  const [loading, setLoading] = useState(false);
  const [failedAvatarIds, setFailedAvatarIds] = useState(() => new Set());

  // Load all available attorneys on component mount
  useEffect(() => {
    const fetchAllAttorneys =

    async () => {
      try {
        setLoading(true);
        const response = await api.get('get_all_attorneys');
        dispatch(setAvailableAttorneys(response.data.attorneys));
      } catch (error) {
        console.error('Error fetching all attorneys:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllAttorneys();
  }, [dispatch]);

  // Load matter-specific attorneys when matter number changes
  useEffect(() => {
    let cancelled = false;
    if (matterNumber) {
      const fetchMatterAttorneys =

      async () => {
        try {
          const response = await api.get(`get_attorneys_for_matter/${matterNumber}`);
          if (cancelled) return;
          const attorneys = response.data.attorneys;
          dispatch(setMatterAttorneys(attorneys));

          // Automatically assign primary attorney if not editing existing event
          if (!currentlyEditingEvent) {
            const primaryAttorney = attorneys.find((att) => att.isPrimary) || attorneys[0];
            dispatch(setAssignedAttorneys(primaryAttorney ? [primaryAttorney] : []));
          }
        } catch (error) {
          console.error('Error fetching matter attorneys:', error);
        }
      };

      fetchMatterAttorneys();
    } else {
      dispatch(setMatterAttorneys([]));
      if (!currentlyEditingEvent) {
        dispatch(setAssignedAttorneys([]));
      }
    }
    return () => { cancelled = true; };
  }, [matterNumber, dispatch, currentlyEditingEvent]);

  // Set assigned attorneys when editing an event (normalize backend shape to UI shape)
  useEffect(() => {
    if (currentlyEditingEvent && Array.isArray(currentlyEditingEvent.assignees)) {
      const normalizedAssignees = currentlyEditingEvent.assignees.map((a) => ({
        id: a.attorney_id,
        name: a.attorney_name,
        profilePic: a.attorney_profile_pic,
        isPrimary: a.is_primary
      }));
      dispatch(setAssignedAttorneys(normalizedAssignees));
    }
  }, [currentlyEditingEvent, dispatch]);

  const handleAssigneeChange =

  (selectedAttorneys) => {
    dispatch(setAssignedAttorneys(selectedAttorneys));
  };

  const removeAssignee =

  (attorneyToRemove) => {
    const updatedAssignees = assignedAttorneys.filter((att) => att.id !== attorneyToRemove.id);
    dispatch(setAssignedAttorneys(updatedAssignees));
  };

  const addPrimaryAttorney =

  () => {
    const primaryAttorney = matterAttorneys.find((att) => att.isPrimary);
    if (primaryAttorney && !assignedAttorneys.find((att) => att.id === primaryAttorney.id)) {
      dispatch(setAssignedAttorneys([...assignedAttorneys, primaryAttorney]));
    }
  };

  const addAllMatterAttorneys =

  () => {
    const newAssignees = [...assignedAttorneys];
    matterAttorneys.forEach((attorney) => {
      if (!newAssignees.find((assigned) => assigned.id === attorney.id)) {
        newAssignees.push(attorney);
      }
    });
    dispatch(setAssignedAttorneys(newAssignees));
  };

  const attorneyItemTemplate =

  (option) => {
    const isPrimary = matterAttorneys.find((att) => att.id === option.id)?.isPrimary;
    return (
      <div className="flex align-items-center">
                <span className="mr-2">{option.name}</span>
                {isPrimary &&
        <Badge value="Primary" severity="info" size="small" />}

                {matterAttorneys.find((att) => att.id === option.id) && !isPrimary &&
        <Badge value="Case Attorney" severity="secondary" size="small" />}

            </div>);

  };

  const primaryAttorney = matterAttorneys.find((att) => att.isPrimary);
  const hasPrimaryAssigned = assignedAttorneys.find((att) => att.id === primaryAttorney?.id);



  return (
    <div className="field mb-3 col-12">
            <label htmlFor="assignees" className="font-medium text-900 mb-2 block">
                Assignees
                {matterNumber &&
        <span className="text-500 text-xs ml-2">
                        (auto-assigned for {matterNumber})
                    </span>}

            </label>

            {/* Display currently assigned attorneys - compact chip layout with avatars */}
            {assignedAttorneys.length > 0 &&
      <div className="mb-2">
                    <div className="flex flex-wrap gap-1">
                        {assignedAttorneys.map(

                        (attorney) => {
            const isPrimary = matterAttorneys.find((att) => att.id === attorney.id)?.isPrimary;
            const hasProfilePic = attorney.profilePic &&
            typeof attorney.profilePic === 'string' &&
            attorney.profilePic.trim() !== '' &&
            !failedAvatarIds.has(attorney.id);

            const getInitials =

            (name) => {
              if (!name) return '?';
              return name.split(' ')
              .map((n) => n[0])
              .join('')
              .substring(0, 2)
              .toUpperCase();
            };

            return (
              <div
                key={attorney.id}
                className={`${isPrimary ? 'p-chip-primary' : ''} glass-chip assignee-chip`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '4px 12px',
                  borderRadius: '16px',
                  backgroundColor: 'var(--surface-100)',
                  border: '1px solid var(--surface-300)',
                  fontSize: '14px',
                  position: 'relative'
                }}>

                                    {hasProfilePic ?
                <img
                  src={attorney.profilePic}
                  alt={attorney.name}
                  referrerPolicy="no-referrer"
                  crossOrigin="anonymous"
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    setFailedAvatarIds((current) => new Set(current).add(attorney.id));
                  }} /> :


                isPrimary ? <AppIcon icon="pi pi-star-fill"
                style={{ fontSize: '14px' }} /> :

                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: '#6366f1',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '9px',
                  fontWeight: 'bold'
                }}>
                                                {getInitials(attorney.name)}
                                            </div>}


                                    <span>{attorney.name}</span>
                                    <AppIcon icon="pi pi-times"

                style={{
                  fontSize: '12px',
                  cursor: 'pointer',
                  marginLeft: '4px'
                }}
                onClick={() => removeAssignee(attorney)} />

                                </div>);

          })}
                    </div>
                </div>}


            {/* Attorney selection dropdown */}
            <MultiSelect
        id="assignees"
        value={assignedAttorneys}
        onChange={(e) => handleAssigneeChange(e.value)}
        options={availableAttorneys}
        optionLabel="name"
        placeholder="Select assignees..."
        itemTemplate={attorneyItemTemplate}
        className="w-full"
        disabled={loading}
        filter
        showClear />


            {/* Quick action buttons - compact layout */}
            {matterNumber && matterAttorneys.length > 0 &&
      <div className="mt-2 flex gap-2 flex-wrap">
                    {primaryAttorney && !hasPrimaryAssigned &&
        <Button
          label={`Add Primary (${primaryAttorney.name})`}
          icon="pi pi-star"
          size="small"
          severity="info"
          outlined
          onClick={addPrimaryAttorney} />}


                    {matterAttorneys.length > assignedAttorneys.filter((assigned) =>
        matterAttorneys.find((matter) => matter.id === assigned.id)
        ).length &&
        <Button
          label="Add All Case Attorneys"
          icon="pi pi-users"
          size="small"
          severity="secondary"
          outlined
          onClick={addAllMatterAttorneys} />}


                </div>}


            {/* Information text */}
            {!matterNumber &&
      <small className="text-500 mt-1 block text-xs">
                    Select a matter to auto-assign primary attorney
                </small>}

        </div>);

};

export default AssigneeManagement;
